import React, { useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Home, ExternalLink, MapPin } from 'lucide-react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import '@/styles/map.css';
import { cn } from "@/lib/utils";

// Helper to format price for pins (e.g., $250k)
const formatMapPrice = (price) => {
    if (!price) return 'S/P';
    if (price >= 1000000) return `$${(price / 1000000).toFixed(1)}M`;
    if (price >= 1000) return `$${(price / 1000).toFixed(0)}k`;
    return `$${price}`;
};

const createCustomIcon = (price, operation) => {
    const isRent = operation?.toLowerCase().includes('alquiler') || operation?.toLowerCase().includes('renta');
    return L.divIcon({
        className: 'custom-pin-container',
        html: `
            <div class="group relative flex flex-col items-center">
                <div class="custom-pin shadow-2xl transition-all duration-300 group-hover:scale-110 group-hover:-translate-y-1 ${isRent ? 'bg-indigo-600' : 'bg-blue-600'}">
                    ${formatMapPrice(price)}
                </div>
                <div class="w-2 h-2 rounded-full border-2 border-white ${isRent ? 'bg-indigo-600' : 'bg-blue-600'} -mt-1 shadow-md"></div>
            </div>
        `,
        iconSize: [60, 40],
        iconAnchor: [30, 40],
        popupAnchor: [0, -40]
    });
};

const MapUpdater = ({ properties, focusedLocation }) => {
    const map = useMap();

    useEffect(() => {
        if (focusedLocation && Array.isArray(focusedLocation) && focusedLocation.length === 2) {
            try {
                map.flyTo(focusedLocation, 16, { duration: 2, easeLinearity: 0.25 });
            } catch (error) {
                console.warn('Map flyTo error:', error);
            }
        }
    }, [focusedLocation, map]);

    useEffect(() => {
        if (!focusedLocation && Array.isArray(properties) && properties.length > 0) {
            try {
                const validCoords = properties
                    .filter(p => p.ubicacion?.latitud && p.ubicacion?.longitud)
                    .map(p => [p.ubicacion.latitud, p.ubicacion.longitud]);

                if (validCoords.length > 0) {
                    const bounds = L.latLngBounds(validCoords);
                    map.fitBounds(bounds, { padding: [80, 80], maxZoom: 15 });
                }
            } catch (error) {
                console.warn('Map fitBounds error:', error);
            }
        }
    }, [properties, map, focusedLocation]);

    return null;
};

const InteractiveMap = ({ properties = [], focusedLocation }) => {
    const defaultCenter = [13.994, -89.559];

    const validProperties = Array.isArray(properties)
        ? properties.filter(p =>
            p?.ubicacion?.latitud &&
            p?.ubicacion?.longitud &&
            !isNaN(p.ubicacion.latitud) &&
            !isNaN(p.ubicacion.longitud)
        )
        : [];

    return (
        <div className="h-full w-full rounded-3xl overflow-hidden shadow-2xl border border-white/20 relative z-0 group/map" style={{ minHeight: '500px' }}>
            {/* Map UI Overlay */}
            <div className="absolute top-6 left-6 z-[1000] flex flex-col gap-2 pointer-events-none">
                <div className="bg-white/90 dark:bg-slate-900/90 backdrop-blur-md px-4 py-2 rounded-2xl shadow-xl flex items-center gap-2 border border-white/20 pointer-events-auto">
                    <MapPin className="w-4 h-4 text-blue-600" />
                    <span className="text-xs font-black uppercase tracking-widest text-slate-800 dark:text-white">Explorador de Zona</span>
                </div>
                <div className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-md px-3 py-1.5 rounded-xl shadow-lg flex items-center gap-3 text-[10px] font-bold border border-white/20 pointer-events-auto">
                    <div className="flex items-center gap-1.5">
                        <div className="w-2 h-2 rounded-full bg-blue-600"></div>
                        <span className="text-slate-600 dark:text-slate-300">Venta</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                        <div className="w-2 h-2 rounded-full bg-indigo-600"></div>
                        <span className="text-slate-600 dark:text-slate-400">Alquiler</span>
                    </div>
                </div>
            </div>

            <MapContainer
                center={defaultCenter}
                zoom={13}
                scrollWheelZoom={false}
                zoomControl={false}
                style={{ height: '100%', width: '100%', minHeight: '500px' }}
                whenCreated={(mapInstance) => {
                    setTimeout(() => {
                        mapInstance.invalidateSize();
                    }, 100);
                }}
            >
                <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.fr/hot/{z}/{x}/{y}.png"
                    detectRetina={true}
                />
                <MapUpdater properties={validProperties} focusedLocation={focusedLocation} />

                {validProperties.map(property => (
                    <Marker
                        key={property.id}
                        position={[property.ubicacion.latitud, property.ubicacion.longitud]}
                        icon={createCustomIcon(property.precio?.monto || 0, property.operacion)}
                    >
                        <Popup closeButton={false} className="custom-popup-premium">
                            <div className="w-64 overflow-hidden rounded-2xl bg-white dark:bg-slate-900 shadow-2xl border border-slate-100 dark:border-slate-800 animate-in zoom-in-95 duration-200">
                                <div className="relative aspect-video w-full overflow-hidden">
                                    <img
                                        className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                                        alt={property.titulo}
                                        src={property.image_url || property.multimedia?.fotos?.[0]?.url || "https://images.unsplash.com/photo-1564013799919-ab600027ffc6"}
                                        onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1512917774080-9991f1c4c750"; }}
                                    />
                                    <Badge className="absolute top-3 left-3 bg-white/90 text-slate-900 border-0 text-[10px] font-black uppercase">
                                        {property.operacion}
                                    </Badge>
                                    <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/80 to-transparent">
                                        <p className="text-white font-black text-xl tracking-tighter">
                                            <span className="text-blue-400 text-sm">$</span>
                                            {(property.precio?.monto || 0).toLocaleString()}
                                        </p>
                                    </div>
                                </div>
                                <div className="p-4 space-y-3">
                                    <div>
                                        <h3 className="font-black text-slate-900 dark:text-white text-sm leading-tight line-clamp-1">{property.titulo}</h3>
                                        <p className="text-[10px] font-bold text-slate-500 mt-0.5 flex items-center gap-1 uppercase tracking-tighter text-blue-600">
                                            <MapPin className="w-3 h-3" /> {property.ubicacion?.zona || 'Sin Zona'}
                                        </p>
                                    </div>

                                    <div className="flex items-center gap-4 text-[10px] font-black text-slate-400 uppercase tracking-widest pt-2 border-t border-slate-50 dark:border-slate-800">
                                        <div className="flex items-center gap-1">
                                            <span className="text-slate-900 dark:text-white">{property.bedrooms || 0}</span> Hab
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <span className="text-slate-900 dark:text-white">{property.bathrooms || 0}</span> Baños
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <span className="text-slate-900 dark:text-white">{property.dimensiones?.construccion || 0}</span> m²
                                        </div>
                                    </div>

                                    <Link to={`/propiedad/${property.id}`} className="block">
                                        <Button className="w-full bg-blue-600 hover:bg-black text-white font-black rounded-xl h-10 shadow-lg shadow-blue-500/20 transition-all active:scale-95 group">
                                            Explorar <ExternalLink className="w-3 h-3 ml-2 opacity-0 group-hover:opacity-100 transition-opacity" />
                                        </Button>
                                    </Link>
                                </div>
                            </div>
                        </Popup>
                    </Marker>
                ))}
            </MapContainer>
        </div>
    );
};

export default InteractiveMap;
